import React, { useEffect, useState } from "react";
import AppHeader from "@/components/AppHeader";
import CommunitiesSection from "@/components/CommunitiesSection";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Search, Shield, UserPlus, Check, Circle } from "lucide-react";
import TierBadge from "@/components/TierBadge";
import api from "@/lib/api";
import { maskEmail } from "@/lib/mask";
import { displayName } from "@/lib/displayName";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

// "Ultima volta on-line" formatter — relative-time for recent activity,
// absolute date for stale ones. Keeps the card tight (max 2 short lines).
const relTime = (iso) => {
  if (!iso) return "Mai visto";
  try {
    const then = new Date(iso);
    const ms = Date.now() - then.getTime();
    const s = Math.floor(ms / 1000);
    if (s < 45) return "poco fa";
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} min fa`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} h fa`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d} g fa`;
    return then.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  } catch { return "—"; }
};

const MeltingPoint = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [boards, setBoards] = useState([]);
  const [busyBoardId, setBusyBoardId] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/users/directory");
        setUsers(r.data);
      } catch { toast.error("Errore caricamento directory"); }
      finally { setLoading(false); }
    })();
  }, []);

  const loadBoards = async () => {
    try {
      const r = await api.get("/boards");
      // Only boards I own (I can only invite to my own boards)
      setBoards(r.data.filter((b) => b.role_in_board === "owner"));
    } catch { toast.error("Errore caricamento schede"); }
  };

  const openPicker = async (u) => {
    if (u.user_id === me?.user_id) { toast.info("Sei tu ;)"); return; }
    setPicked(u);
    await loadBoards();
  };

  const addToBoard = async (board) => {
    setBusyBoardId(board.id);
    try {
      await api.post(`/boards/${board.id}/members`, { user_id: picked.user_id });
      toast.success(`${picked.name || picked.email} aggiunto a "${board.name}"`);
      await loadBoards();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusyBoardId(null); }
  };

  const filtered = users.filter((u) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s);
  });

  const alreadyMember = (board) =>
    picked && (board.owner_id === picked.user_id || (board.member_ids || []).includes(picked.user_id));

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-6xl mx-auto p-6">
        <div className="mb-7">
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> Community
          </div>
          <h1 className="font-display font-bold text-4xl tracking-tighter leading-none mt-1">La community ImmoMap</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            Tutti gli utenti registrati. Clicca su qualcuno per invitarlo come collaboratore in una tua scheda.
          </p>
        </div>

        <CommunitiesSection />

        <div className="ios-card p-4 mb-5">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="mp-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca per nome o email…"
              className="rounded-xl h-11 pl-10 bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-[#007AFF]"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500">Caricamento…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-start">
            {filtered.map((u) => {
              const isMe = u.user_id === me?.user_id;
              const online = !!u.is_online;
              return (
                <button
                  key={u.user_id}
                  onClick={() => openPicker(u)}
                  data-testid={`mp-user-${u.user_id}`}
                  className={`ios-card text-left transition-all hover:-translate-y-0.5 hover:shadow-lg relative p-4 flex flex-col ${
                    online ? "ring-2 ring-emerald-400/60" : ""
                  } ${isMe ? "ring-2 ring-[#007AFF]/40" : ""}`}
                  style={online ? {
                    boxShadow: "0 0 0 2px rgba(52,199,89,0.35), 0 0 24px 4px rgba(52,199,89,0.35), 0 8px 24px -8px rgba(52,199,89,0.4)",
                    background: "linear-gradient(180deg, rgba(220,252,231,0.55) 0%, #ffffff 40%)",
                  } : undefined}
                  data-online={online ? "1" : "0"}
                >
                  {online && (
                    <span
                      className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full pl-1 pr-2 py-0.5 text-[9px] uppercase tracking-widest font-bold text-white shadow-sm z-10"
                      style={{ background: "linear-gradient(135deg, #34C759, #22a34c)" }}
                      data-testid={`mp-online-badge-${u.user_id}`}
                    >
                      <Circle className="w-2 h-2 text-white fill-white animate-pulse" strokeWidth={0} />
                      Online
                    </span>
                  )}
                  {/* Top section: avatar + identity ------------------------ */}
                  <div className="flex items-start gap-3">
                    <div className="relative shrink-0">
                      {u.picture ? (
                        <img
                          src={u.picture}
                          alt=""
                          className={`rounded-full object-cover w-12 h-12 ${online ? "ring-2 ring-emerald-400" : ""}`}
                        />
                      ) : (
                        <div className={`rounded-full bg-gradient-to-br from-slate-200 to-slate-300 text-slate-700 flex items-center justify-center font-semibold w-12 h-12 text-lg ${online ? "ring-2 ring-emerald-400" : ""}`}>
                          {(u.name || u.email)[0]?.toUpperCase()}
                        </div>
                      )}
                      {/* Icon-only tier badge overlaid on avatar corner —
                          matches the reference mock (medal / clock / star). */}
                      <TierBadge
                        tier={u.tier || "demo"}
                        iconOnly
                        iconSize={22}
                        className="absolute -top-1.5 -right-1.5"
                      />
                    </div>
                    <div className="min-w-0 flex-1 pt-0.5">
                      <div className="font-semibold text-sm text-slate-900 truncate leading-tight" data-testid={`mp-name-${u.user_id}`}>
                        {displayName(u) || maskEmail(u.email).split("@")[0]}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate mt-0.5" data-testid={`mp-email-${u.user_id}`}>
                        {maskEmail(u.email)}
                      </div>
                      <div className={`text-[10px] mt-1 truncate ${online ? "text-emerald-700 font-semibold" : "text-slate-400"}`} data-testid={`mp-lastseen-${u.user_id}`}>
                        {online ? "Online adesso" : `Ultima volta on-line: ${relTime(u.last_seen)}`}
                      </div>
                    </div>
                  </div>

                  {/* Bottom row: role badge (+ "TU") · schede count ---------- */}
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {u.role === "admin" ? (
                        <Badge className="rounded-full bg-[#12466F] text-white text-[9px] uppercase tracking-widest border-0 px-2 py-0.5">
                          <Shield className="w-2.5 h-2.5 mr-1" /> Admin
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="rounded-full text-[9px] uppercase tracking-widest border-slate-300 text-slate-500 px-2 py-0.5">
                          Utente
                        </Badge>
                      )}
                      {isMe && (
                        <span
                          className="text-[9px] uppercase tracking-widest text-[#007AFF] font-bold rounded-full bg-[#007AFF]/10 px-1.5 py-0.5 leading-none"
                          data-testid={`mp-you-${u.user_id}`}
                        >
                          Tu
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-slate-400 font-mono-num shrink-0" data-testid={`mp-boards-${u.user_id}`}>
                      {u.owned_boards_count || 0} schede
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>

      <Dialog open={!!picked} onOpenChange={(v) => !v && setPicked(null)}>
        <DialogContent className="max-w-lg rounded-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-display font-bold text-2xl tracking-tight">
              Invita in una scheda
            </DialogTitle>
          </DialogHeader>
          {picked && (
            <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
              {picked.picture ? (
                <img src={picked.picture} alt="" className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center font-semibold">
                  {(picked.name || picked.email)[0]?.toUpperCase()}
                </div>
              )}
              <div>
                <div className="font-semibold text-sm">{picked.name || picked.email}</div>
                <div className="text-xs text-slate-500">{picked.email}</div>
              </div>
            </div>
          )}
          <div className="space-y-2 max-h-[380px] overflow-y-auto">
            {boards.length === 0 ? (
              <div className="text-sm text-slate-500 py-8 text-center">
                Non hai ancora una scheda. Crea una scheda per invitare collaboratori.
              </div>
            ) : boards.map((b) => {
              const already = alreadyMember(b);
              return (
                <div key={b.id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{b.name}</div>
                    <div className="text-[11px] text-slate-500">
                      {(b.member_ids?.length || 0) + 1} membri · {b.property_count || 0} immobili
                    </div>
                  </div>
                  <Button
                    size="sm"
                    disabled={already || busyBoardId === b.id}
                    onClick={() => addToBoard(b)}
                    className={`rounded-full h-8 ${already ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-[#007AFF] hover:bg-[#0060df] text-white"}`}
                    data-testid={`mp-add-${b.id}`}
                  >
                    {already ? (<><Check className="w-3.5 h-3.5 mr-1" /> Già dentro</>) : (<><UserPlus className="w-3.5 h-3.5 mr-1" /> Invita</>)}
                  </Button>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setPicked(null)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MeltingPoint;
